// Drives async sync of pending IDB records to the API with exponential
// backoff. Owned by IntakeRepository. The apiClient abstraction lets us
// inject a fake in tests and keeps fetch+auth concerns out of this file.

import { MAX_SYNC_RETRIES } from './IndexedDBStore.js';

const TICK_INTERVAL_MS = 5000;
// Backoff per retry_count value: retry_count=0 → wait 0ms (fresh), then
// 1s, 2s, 4s, 8s, 16s after each successive failure.
const BACKOFF_MS = [0, 1000, 2000, 4000, 8000, 16000];

export default class RetryQueue {
  constructor({ idbStore, apiClient, telemetry }) {
    if (!idbStore) throw new Error('idbStore required');
    if (!apiClient || typeof apiClient.syncMessage !== 'function') {
      throw new Error('apiClient with syncMessage() required');
    }
    this._idb = idbStore;
    this._api = apiClient;
    this._telemetry = telemetry || null;
    this._listeners = new Map();
    this._intervalId = null;
    this._running = false;
    this._inFlight = new Set();
  }

  start() {
    if (this._intervalId) return;
    this._intervalId = setInterval(
      () => this.processNow().catch(() => {}),
      TICK_INTERVAL_MS
    );
  }

  stop() {
    if (this._intervalId) {
      clearInterval(this._intervalId);
      this._intervalId = null;
    }
  }

  on(event, callback) {
    if (!this._listeners.has(event)) this._listeners.set(event, new Set());
    this._listeners.get(event).add(callback);
  }

  off(event, callback) {
    const set = this._listeners.get(event);
    if (set) set.delete(callback);
  }

  _emit(event, payload) {
    const set = this._listeners.get(event);
    if (!set) return;
    for (const cb of set) {
      try {
        cb(payload);
      } catch (e) {
        console.error('[RetryQueue] listener threw:', e);
      }
    }
  }

  async processNow() {
    if (this._running) return;
    this._running = true;
    try {
      const pending = await this._idb.getPendingMessages();
      const now = Date.now();
      for (const record of pending) {
        if (this._inFlight.has(record.id)) continue;
        if (!this._isDue(record, now)) continue;
        await this._attempt(record);
      }
    } finally {
      this._running = false;
    }
  }

  async enqueue(messageId) {
    const record = await this._idb.getMessage(messageId);
    if (!record) return;
    if (this._inFlight.has(record.id)) return;
    if (record.sync_status === 'synced') return;
    if ((record.retry_count || 0) >= MAX_SYNC_RETRIES) return;
    await this._attempt(record);
  }

  _isDue(record, now) {
    const retry = record.retry_count || 0;
    if (retry === 0) return true;
    if (!record.last_retry_at) return true;
    const waitMs = BACKOFF_MS[Math.min(retry, BACKOFF_MS.length - 1)];
    const elapsed = now - new Date(record.last_retry_at).getTime();
    return elapsed >= waitMs;
  }

  async _attempt(record) {
    this._inFlight.add(record.id);
    try {
      await this._api.syncMessage(record);
      await this._idb.markSynced(record.id);
      this._emit('syncSuccess', { messageId: record.id });
      this._telemetry?.track('intake.sync.success', {
        messageId: record.id,
        sessionId: record.session_id,
        turnNumber: record.turn_number,
      });
    } catch (error) {
      const updated = await this._idb.markFailed(record.id, error?.message);
      const retryCount = updated?.retry_count ?? (record.retry_count || 0) + 1;
      if (retryCount >= MAX_SYNC_RETRIES) {
        this._emit('syncGiveUp', { messageId: record.id, lastError: error });
        this._telemetry?.track('intake.sync.giveUp', {
          messageId: record.id,
          sessionId: record.session_id,
          turnNumber: record.turn_number,
          lastError: error?.message,
          retryCount,
        });
      } else {
        this._emit('syncFailure', {
          messageId: record.id,
          error,
          retryCount,
        });
        this._telemetry?.track('intake.sync.failure', {
          messageId: record.id,
          sessionId: record.session_id,
          turnNumber: record.turn_number,
          error: error?.message,
          retryCount,
        });
      }
    } finally {
      this._inFlight.delete(record.id);
    }
  }
}
